import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Calendar } from '@fullcalendar/core';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import './CalendarStyles.css';
import { Task, getTaskRequest } from '@models/task.model';
import { getTaskStatusDisplay } from '@utils/taskDisplay';
import { describePrecedentConflicts } from '@utils/precedentConflicts';
import { Event, getEventRequest } from '@models/event.model';
import COLORS from '@utils/color';
import TasksGraphQL from '@graphql/task.queries';
import { graphQlClient } from '@config/graphqlClient';
import EventsGraphQL from '@graphql/event.queries';
import { SnackType } from '@models/common.model';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { useAppDispatch } from '@redux/hooks';
import { useTranslation } from '@hooks/useTranslation';
import { useParams } from 'react-router-dom';
import { useBudget } from '@hooks/api/useBudget';
import {
  parseTaskDate,
  formatTaskDateForDisplay,
  formatTaskDateForBackend,
  getCalendarDateString,
  calculateDaysDifference,
  addDaysToDate,
  toCalendarISOString,
} from '@utils/dateOnlyParser';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { useProjectWeather } from '@hooks/api/weather/useProjectWeather';
import { WeatherLocaleStrings } from './weather/weatherContent';
import { applyWeatherGlyphs, attachWeatherGlyphByKey } from './weather/applyWeatherGlyphs';
import { MoveVertical } from 'lucide-react';
import { sortBySequence } from './utils/taskOrdering';
import { getScheduleAnchorDate } from './utils/getScheduleAnchorDate';
import { computeProjectRange } from './utils/computeProjectRange';
import { PROJECT_VIEW, buildProjectView } from './utils/projectView';
import { clampWeatherRange } from './utils/clampWeatherRange';
import { Skeleton } from '@components/atoms/Skeleton';
import CustomizedTables from './components/TaskAndEventTable';
import { CalendarHeader } from './components/CalendarHeader';
import {
  PrecedentConflictModal,
  PrecedentConflict,
} from '../PrecedentConflictModal';
import {
  SubtaskConflictModal,
  SubtaskConflict,
} from '../SubtaskConflictModal';
import {
  SubtaskOutOfRangeModal,
  SubtaskOutOfRangeData,
} from '../SubtaskOutOfRangeModal';
import {
  DelegatedTaskModal,
  DelegatedTaskData,
} from '../DelegatedTaskModal';

interface Resource {
  id: string;
  title: string;
  start: string;
  end: string;
  isEvent: boolean;
  status?: string | null;
  children?: Resource[];
  sortOrder?: number;
  extendedProps?: any;
  superintendentId?: string;
  presedentTaskNames?: string[];
}

interface Evento {
  id: string;
  title: string;
  start: string;
  end: string;
  resourceId: string;
  color: string;
  textColor: string;
  isEvent: boolean;
  status?: string | null;
}

const DEP_POPOVER_ID = 'resource-dep-popover-portal';

const escapeHtmlAttr = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const hideDepPopover = () => {
  const existing = document.getElementById(DEP_POPOVER_ID);
  if (existing) existing.remove();
};

const showDepPopover = (badgeEl: HTMLElement) => {
  hideDepPopover();
  let depNames: string[] = [];
  try {
    depNames = JSON.parse(badgeEl.getAttribute('data-deps') || '[]');
  } catch {
    depNames = [];
  }
  if (!depNames.length) return;

  const popover = document.createElement('div');
  popover.id = DEP_POPOVER_ID;
  popover.className = 'resource-dep-popover-portal';
  popover.setAttribute('role', 'tooltip');
  popover.innerHTML = `
    <span class="resource-dep-popover__title">Depends on</span>
    <ul class="resource-dep-popover__list">${depNames
      .map((name) => `<li>${escapeHtmlAttr(name)}</li>`)
      .join('')}</ul>
  `;
  document.body.appendChild(popover);

  const badgeRect = badgeEl.getBoundingClientRect();
  const popoverRect = popover.getBoundingClientRect();
  const margin = 8;

  let top = badgeRect.bottom + 6;
  if (top + popoverRect.height > window.innerHeight - margin) {
    top = Math.max(margin, badgeRect.top - popoverRect.height - 6);
  }

  let left = badgeRect.right - popoverRect.width;
  if (left < margin) left = margin;
  if (left + popoverRect.width > window.innerWidth - margin) {
    left = window.innerWidth - popoverRect.width - margin;
  }

  popover.style.top = `${top}px`;
  popover.style.left = `${left}px`;

  requestAnimationFrame(() => popover.classList.add('is-visible'));
};

interface TimeLineComponentProps {
  flag?: boolean;
  tasks?: Task[];
  events: Event[];
  projectId: string | undefined;
  projectStartDate?: string | Date | null;
  projectEndDate?: string | Date | null;
  isDelegated: boolean;
  setTask: (id: string) => void;
  setTaskDetails?: (id: string) => void;
  setEvent: (id: string, events: Event[]) => void;
}

interface RouteParams {
  id: string;
  status: string;
}

export const Timeline = forwardRef<any, TimeLineComponentProps>(
  (
    {
      tasks,
      events,
      setTask,
      setTaskDetails,
      setEvent,
      projectId,
      projectStartDate,
      projectEndDate,
      isDelegated,
      flag = false,
    },
    ref,
  ) => {
    const calendarRef = useRef<HTMLDivElement | null>(null);
    const calendarApiRef = useRef<Calendar | null>(null);

    const [tasksLocal, setTasksLocal] = useState<Task[]>([]);
    const [eventsLocal, setEventsLocal] = useState<Event[]>([]);

    // Refs to hold current state values for use in event handlers (avoids stale closure)
    const tasksLocalRef = useRef<Task[]>([]);
    const eventsLocalRef = useRef<Event[]>([]);
    const [organizedTasks, setOrganizedTasks] = useState<Task[]>([]);

    const dispatch = useAppDispatch();
    const localeCommon = useTranslation('common');
    const tSchedule = useTranslation('schedule');
    const { status } = useParams<RouteParams>();
    // Story-209: the whole-project view is the default when a project's schedule opens.
    const [view, setView] = useState<string>(PROJECT_VIEW);
    // Gate for the Project view: set once the first task fetch resolves (or the `tasks` prop
    // arrives defined) so the range is never built from empty tasks ("today to +10" flash).
    const [tasksLoaded, setTasksLoaded] = useState<boolean>(false);
    const [showTaskList, setShowTaskList] = useState<boolean>(false);
    const [currentView, setCurrentView] = useState<
      'timeline' | 'calendar' | 'taskList'
    >('timeline');
    const [calendarTitle, setCalendarTitle] = useState<string>('');
    const [checkedAccept] = useState<boolean>(false);
    const [checkedCancel] = useState<boolean>(false);
    const [checkedOnGoing] = useState<boolean>(true);

    // Timeline date range state
    const [timelineStartDate, setTimelineStartDate] = useState<string | null>(
      null,
    );
    const [timelineEndDate, setTimelineEndDate] = useState<string | null>(null);

    // Story-209 (D9). The Project view's range is DERIVED from the project dates + current tasks,
    // never written by a drag/resize/create handler: every path that changes tasks ends in
    // `setTasksLocal`, so this memo (and the one effect that applies it) covers them all.
    const projectRange = useMemo(
      () =>
        tasksLoaded
          ? computeProjectRange({ projectStartDate, projectEndDate, tasks: tasksLocal })
          : null,
      [tasksLoaded, projectStartDate, projectEndDate, tasksLocal],
    );
    // Read by the Project view's `visibleRange` function (see `buildProjectView`), so it must be
    // current before FullCalendar evaluates it.
    const projectRangeRef = useRef(projectRange);
    projectRangeRef.current = projectRange;
    // The `start|end` the mounted calendar was last told about.
    const appliedProjectRangeRef = useRef<string | null>(null);
    const projectReady = projectRange !== null;

    // Where the calendar opens. Seeded from the project dates alone; refined
    // once tasks arrive (see the ANCHOR effect below).
    const anchorDateRef = useRef<string>(
      getScheduleAnchorDate({ projectStartDate, projectEndDate }),
    );
    // One-shot override for the next calendar (re)initialisation. The Today
    // button and task-list date links clear the custom range, which rebuilds
    // the calendar; without this the rebuild would land on the anchor instead.
    const pendingInitialDateRef = useRef<string | null>(null);
    // Once the user has paged, jumped or picked a range, stop auto-anchoring.
    const userNavigatedRef = useRef<boolean>(false);

    // --- Weather (Sprint 25, story-133) ---
    // The visible date range, tracked via the calendar's own `datesSet` hook so the weather
    // query always matches what's actually on screen (initial paint, prev/next, view switch).
    const [weatherVisibleRange, setWeatherVisibleRange] = useState<{
      from: string;
      to: string;
    } | null>(null);
    const { data: weatherByDate } = useProjectWeather({
      projectId,
      from: weatherVisibleRange?.from ?? null,
      to: weatherVisibleRange?.to ?? null,
    });
    // Read via ref (never a stale closure) by the `slotLabelDidMount`/`dayCellDidMount`
    // callbacks captured inside the calendar-init effect below, which does not re-run on every
    // weather fetch. Those hooks only cover cells mounted *after* this ref is populated —
    // FullCalendar's `Calendar#render()` does NOT re-invoke DidMount on cells already on screen,
    // so cells mounted before weather data resolves (the normal case, since the query is never
    // awaited before first paint) are handled by the `applyWeatherGlyphs` effect below instead.
    const weatherByDateRef = useRef<Map<string, any>>(new Map());
    const { activeLocale: weatherActiveLocale } = useAppSelector(
      (state: RootState) => state.locale,
    );
    const weatherLocaleStrings = useTranslation('schedule') as WeatherLocaleStrings;
    const weatherDateLocale = weatherActiveLocale === 'es' ? 'es-ES' : 'en-US';

    useEffect(() => {
      weatherByDateRef.current = weatherByDate ?? new Map();

      // Re-attach glyphs directly onto whatever cells are already mounted in THIS calendar's own
      // container (never `document` — multiple project accordions can each hold a mounted
      // calendar instance at once). Deliberately does not call `calendarApiRef.current?.render()`
      // or otherwise touch the `Calendar` instance: that would risk resetting scroll position,
      // selection, or navigation state, and does not re-fire DidMount anyway (see comment above).
      const root = calendarRef.current;
      if (!root) return;
      applyWeatherGlyphs(root, view, {
        weatherByDate: weatherByDateRef.current,
        strings: weatherLocaleStrings,
        dateLocale: weatherDateLocale,
      });
    }, [weatherByDate, view, weatherLocaleStrings, weatherDateLocale]);

    // Budget requests state
    const [taskBudgetRequests, setTaskBudgetRequests] = useState<
      Map<string, any[]>
    >(new Map());
    const { fetchBudgetRequestsByTaskId } = useBudget();

    // Precedent conflict modal state
    const [showPrecedentConflictModal, setShowPrecedentConflictModal] =
      useState(false);
    const [precedentConflicts, setPrecedentConflicts] = useState<
      PrecedentConflict[]
    >([]);
    const [pendingDropData, setPendingDropData] = useState<{
      taskId: string;
      newStartDate: Date;
      newEndDate: Date;
    } | null>(null);

    // Drag visual feedback state
    const [isDragging, setIsDragging] = useState(false);
    const [draggingTaskName, setDraggingTaskName] = useState<string>('');

    // Loading state for drag-drop updates
    const [isUpdatingTask, setIsUpdatingTask] = useState(false);

    // Subtask conflict modal state (for when parent task is moved/resized and subtasks fall outside)
    const [showSubtaskConflictModal, setShowSubtaskConflictModal] = useState(false);
    const [subtaskConflicts, setSubtaskConflicts] = useState<SubtaskConflict[]>([]);
    const [subtaskConflictMode, setSubtaskConflictMode] = useState<'move' | 'resize'>('move');
    const [pendingSubtaskDropData, setPendingSubtaskDropData] = useState<{
      taskId: string;
      newStartDate: Date;
      newEndDate: Date;
      oldStartDate: Date;
      subtasksToMove: Task[];
    } | null>(null);

    // Subtask out of range modal state (for when subtask is moved/resized outside parent)
    const [showSubtaskOutOfRangeModal, setShowSubtaskOutOfRangeModal] = useState(false);
    const [subtaskOutOfRangeData, setSubtaskOutOfRangeData] = useState<SubtaskOutOfRangeData | null>(null);
    const [subtaskOutOfRangeMode, setSubtaskOutOfRangeMode] = useState<'move' | 'resize'>('move');

    // Delegated task modal state
    const [showDelegatedTaskModal, setShowDelegatedTaskModal] = useState(false);
    const [delegatedTaskData, setDelegatedTaskData] = useState<DelegatedTaskData | null>(null);
    const [pendingDelegatedDropData, setPendingDelegatedDropData] = useState<{
      taskId: string;
      newStartDate: Date;
      newEndDate: Date;
    } | null>(null);

    // Check if a task is delegated
    const checkDelegatedTask = (
      taskId: string,
      tasksList: Task[],
    ): DelegatedTaskData | null => {
      const task = tasksList.find((t) => t.taskId === taskId);

      if (!task) {
        return null;
      }

      // A task is delegated if it has delegateOrganizationId or delegateTaskId
      const isDelegated = !!(task.delegateOrganizationId || task.delegateTaskId);

      if (!isDelegated) {
        return null;
      }

      return {
        taskId: task.taskId!,
        taskName: task.name,
        delegateOrganizationName: task.delegateOrganization?.name,
      };
    };

    // Handler for when user cancels the delegated task modal
    const handleDelegatedTaskCancel = () => {
      setShowDelegatedTaskModal(false);
      setDelegatedTaskData(null);
      setPendingDelegatedDropData(null);
    };

    // Handler for when user confirms moving the delegated task
    const handleDelegatedTaskConfirm = async () => {
      if (!pendingDelegatedDropData) return;

      const { taskId, newStartDate, newEndDate } = pendingDelegatedDropData;

      // Continue with the normal flow - check other validations
      const precedentConflictsList = checkPrecedentConflicts(
        taskId,
        newStartDate,
        tasksLocalRef.current,
        eventsLocalRef.current,
      );

      if (precedentConflictsList.length > 0) {
        setShowDelegatedTaskModal(false);
        setDelegatedTaskData(null);
        setPendingDropData({
          taskId,
          newStartDate,
          newEndDate,
        });
        setPrecedentConflicts(precedentConflictsList);
        setShowPrecedentConflictModal(true);
        setPendingDelegatedDropData(null);
        return;
      }

      // Check subtask out of range
      const subtaskOutOfRange = checkSubtaskOutOfParentRange(
        taskId,
        newStartDate,
        newEndDate,
        tasksLocalRef.current,
      );

      if (subtaskOutOfRange) {
        setShowDelegatedTaskModal(false);
        setDelegatedTaskData(null);
        setSubtaskOutOfRangeData(subtaskOutOfRange);
        setPendingSubtaskOutOfRangeDropData({
          subtaskId: taskId,
          newStartDate,
          newEndDate,
        });
        setSubtaskOutOfRangeMode('move');
        setShowSubtaskOutOfRangeModal(true);
        setPendingDelegatedDropData(null);
        return;
      }

      // Check subtask conflicts
      const subtaskConflictsList = checkSubtaskConflicts(
        taskId,
        newStartDate,
        newEndDate,
        tasksLocalRef.current,
      );

      if (subtaskConflictsList.length > 0) {
        const movedTask = tasksLocalRef.current.find((t) => t.taskId === taskId);
        const oldStartDate = movedTask?.startTime
          ? parseTaskDate(movedTask.startTime) || new Date()
          : new Date();
        const subtasksToMove = tasksLocalRef.current.filter(
          (t) => t.parentTaskId === taskId,
        );

        setShowDelegatedTaskModal(false);
        setDelegatedTaskData(null);
        setSubtaskConflicts(subtaskConflictsList);
        setPendingSubtaskDropData({
          taskId,
          newStartDate,
          newEndDate,
          oldStartDate,
          subtasksToMove,
        });
        setSubtaskConflictMode('move');
        setShowSubtaskConflictModal(true);
        setPendingDelegatedDropData(null);
        return;
      }

      // No other conflicts - proceed with update
      setShowDelegatedTaskModal(false);
      setDelegatedTaskData(null);
      setPendingDelegatedDropData(null);

      await updateTaskDates(taskId, newStartDate, newEndDate);
    };

    // Check for precedent conflicts when moving a task
    const checkPrecedentConflicts = (
      taskId: string,
      newStartDate: Date,
      tasksList: Task[],
      eventsList: Event[],
    ): PrecedentConflict[] => {
      const task = tasksList.find((t) => t.taskId === taskId);

      if (!task) {
        return [];
      }

      const conflicts: PrecedentConflict[] = [];

      // Check precedent tasks
      if (task.presedentTaskId && task.presedentTaskId.length > 0) {
        for (const precedentId of task.presedentTaskId) {
          const precedentTask = tasksList.find((t) => t.taskId === precedentId);
          if (precedentTask?.endTime) {
            const precedentEndDate = new Date(precedentTask.endTime);
            if (newStartDate < precedentEndDate) {
              conflicts.push({
                type: 'task',
                id: precedentTask.taskId!,
                name: precedentTask.name,
                endDate: precedentTask.endTime as string,
                conflictReason:
                  'This task must complete before the moved task can start.',
              });
            }
          }
        }
      }

      // Check precedent events
      if (task.presedentEventId && task.presedentEventId.length > 0) {
        for (const precedentId of task.presedentEventId) {
          const precedentEvent = eventsList.find(
            (e) => e.eventId === precedentId,
          );
          if (precedentEvent?.eventTime) {
            const eventDate = new Date(precedentEvent.eventTime);
            if (newStartDate < eventDate) {
              conflicts.push({
                type: 'event',
                id: precedentEvent.eventId!,
                name: precedentEvent.name,
                endDate: precedentEvent.eventTime as string,
                conflictReason:
                  'This event must occur before the moved task can start.',
              });
            }
          }
        }
      }

      return conflicts;
    };

    // Check for subtask conflicts when moving/resizing a parent task
    const checkSubtaskConflicts = (
      taskId: string,
      newStartDate: Date,
      newEndDate: Date,
      tasksList: Task[],
    ): SubtaskConflict[] => {
      const conflicts: SubtaskConflict[] = [];

      // Find all subtasks of this parent task
      const subtasks = tasksList.filter((t) => t.parentTaskId === taskId);

      for (const subtask of subtasks) {
        if (!subtask.startTime || !subtask.endTime) continue;

        const subtaskStart = parseTaskDate(subtask.startTime);
        const subtaskEnd = parseTaskDate(subtask.endTime);

        if (!subtaskStart || !subtaskEnd) continue;

        // Check if subtask falls outside the new parent range
        if (subtaskStart < newStartDate) {
          conflicts.push({
            subtaskId: subtask.taskId!,
            subtaskName: subtask.name,
            currentStartDate: subtask.startTime as string,
            currentEndDate: subtask.endTime as string,
            conflictType: 'before_start',
          });
        } else if (subtaskEnd > newEndDate) {
          conflicts.push({
            subtaskId: subtask.taskId!,
            subtaskName: subtask.name,
            currentStartDate: subtask.startTime as string,
            currentEndDate: subtask.endTime as string,
            conflictType: 'after_end',
          });
        }
      }

      return conflicts;
    };

    // Check if a subtask is being moved/resized outside its parent's range
    const checkSubtaskOutOfParentRange = (
      subtaskId: string,
      newStartDate: Date,
      newEndDate: Date,
      tasksList: Task[],
    ): SubtaskOutOfRangeData | null => {
      const subtask = tasksList.find((t) => t.taskId === subtaskId);

      if (!subtask || !subtask.parentTaskId) {
        return null; // Not a subtask
      }

      const parentTask = tasksList.find((t) => t.taskId === subtask.parentTaskId);

      if (!parentTask || !parentTask.startTime || !parentTask.endTime) {
        return null;
      }

      const parentStart = parseTaskDate(parentTask.startTime);
      const parentEnd = parseTaskDate(parentTask.endTime);

      if (!parentStart || !parentEnd) {
        return null;
      }

      // Determine conflict type
      const beforeStart = newStartDate < parentStart;
      const afterEnd = newEndDate > parentEnd;

      if (!beforeStart && !afterEnd) {
        return null; // No conflict
      }

      let conflictType: 'before_start' | 'after_end' | 'both';
      if (beforeStart && afterEnd) {
        conflictType = 'both';
      } else if (beforeStart) {
        conflictType = 'before_start';
      } else {
        conflictType = 'after_end';
      }

      return {
        subtaskId: subtask.taskId!,
        subtaskName: subtask.name,
        parentTaskId: parentTask.taskId!,
        parentTaskName: parentTask.name,
        parentStartDate: parentTask.startTime as string,
        parentEndDate: parentTask.endTime as string,
        subtaskNewStartDate: formatTaskDateForBackend(newStartDate) || '',
        subtaskNewEndDate: formatTaskDateForBackend(newEndDate) || '',
        conflictType,
      };
    };

    // Handler for when user cancels the conflict modal (OK button)
    const handleConflictCancel = () => {
      setShowPrecedentConflictModal(false);
      setPendingDropData(null);
      setPrecedentConflicts([]);
    };

    // Function to update task dates directly (for drag and drop)
    const updateTaskDates = async (
      taskId: string,
      newStartDate: Date,
      newEndDate: Date,
      options?: { skipParentDateAdjustment?: boolean },
    ): Promise<boolean> => {
      setIsUpdatingTask(true);
      try {
        // Format dates as YYYY-MM-DD for the API
        const startDateStr = formatTaskDateForBackend(newStartDate);
        const endDateStr = formatTaskDateForBackend(newEndDate);

        // Validate dates are properly formatted
        if (!startDateStr || !endDateStr) {
          dispatch(
            appendActionMessage({
              message: tSchedule.invalidDateFormat,
              type: SnackType.ERROR,
            }),
          );
          return false;
        }

        await graphQlClient.request(TasksGraphQL.mutations.updateTask, {
          task: {
            taskId,
            startTime: startDateStr,
            endTime: endDateStr,
            ...(options?.skipParentDateAdjustment && { skipParentDateAdjustment: true }),
          },
        });

        dispatch(
          appendActionMessage({
            message: localeCommon['updatedSuccessfully'] || 'Task updated successfully',
            type: SnackType.SUCCESS,
          }),
        );

        // Reload tasks to reflect the changes
        getTasks(true, projectId);
        return true;
      } catch (e: any) {
        dispatch(
          appendActionMessage({
            message:
              e?.response?.errors[0]?.message || localeCommon['requestError'],
            type: SnackType.ERROR,
          }),
        );
        return false;
      } finally {
        setIsUpdatingTask(false);
      }
    };

    // Handler for when user confirms moving despite conflicts (MOVE ANYWAYS button)
    const handleConflictConfirm = async () => {
      if (pendingDropData) {
        await updateTaskDates(
          pendingDropData.taskId,
          pendingDropData.newStartDate,
          pendingDropData.newEndDate,
        );
      }
      setShowPrecedentConflictModal(false);
      setPendingDropData(null);
      setPrecedentConflicts([]);
    };

    // ===== Subtask Conflict Modal Handlers (parent moved/resized) =====

    // Handler: Move subtasks with parent (shift by same delta)
    const handleAdjustSubtasks = async () => {
      if (!pendingSubtaskDropData) return;

      setIsUpdatingTask(true);
      try {
        const { taskId, newStartDate, newEndDate, oldStartDate, subtasksToMove } = pendingSubtaskDropData;

        // Calculate delta days
        const deltaDays = calculateDaysDifference(oldStartDate, newStartDate);

        // Update parent task first
        const parentSuccess = await updateTaskDates(taskId, newStartDate, newEndDate);
        if (!parentSuccess) {
          return;
        }

        // For resize mode, adjust subtasks to fit within new parent range
        if (subtaskConflictMode === 'resize') {
          // Truncate subtasks to fit within new parent range
          for (const subtask of subtasksToMove) {
            const subtaskStart = parseTaskDate(subtask.startTime);
            const subtaskEnd = parseTaskDate(subtask.endTime);

            if (!subtaskStart || !subtaskEnd) continue;

            let adjustedStart = subtaskStart;
            let adjustedEnd = subtaskEnd;

            // Truncate to fit within parent range
            if (adjustedStart < newStartDate) {
              adjustedStart = newStartDate;
            }
            if (adjustedEnd > newEndDate) {
              adjustedEnd = newEndDate;
            }

            // Only update if dates changed
            if (adjustedStart !== subtaskStart || adjustedEnd !== subtaskEnd) {
              await updateTaskDates(subtask.taskId!, adjustedStart, adjustedEnd);
            }
          }
        } else {
          // For move mode, shift subtasks by the same delta
          for (const subtask of subtasksToMove) {
            const subtaskStart = parseTaskDate(subtask.startTime);
            const subtaskEnd = parseTaskDate(subtask.endTime);

            if (!subtaskStart || !subtaskEnd) continue;

            const newSubtaskStart = addDaysToDate(subtaskStart, deltaDays);
            const newSubtaskEnd = addDaysToDate(subtaskEnd, deltaDays);

            await updateTaskDates(subtask.taskId!, newSubtaskStart, newSubtaskEnd);
          }
        }

        dispatch(
          appendActionMessage({
            message: tSchedule.taskAndSubtasksUpdated,
            type: SnackType.SUCCESS,
          }),
        );

        // Reload tasks
        getTasks(true, projectId);
      } catch (e: any) {
        dispatch(
          appendActionMessage({
            message: e?.response?.errors[0]?.message || localeCommon['requestError'],
            type: SnackType.ERROR,
          }),
        );
      } finally {
        setIsUpdatingTask(false);
        setShowSubtaskConflictModal(false);
        setPendingSubtaskDropData(null);
        setSubtaskConflicts([]);
      }
    };

    // Handler: Leave subtasks at current dates
    const handleLeaveSubtasks = async () => {
      if (!pendingSubtaskDropData) return;

      const { taskId, newStartDate, newEndDate } = pendingSubtaskDropData;

      // Only update parent task
      await updateTaskDates(taskId, newStartDate, newEndDate);

      setShowSubtaskConflictModal(false);
      setPendingSubtaskDropData(null);
      setSubtaskConflicts([]);
    };

    // Handler: Cancel subtask conflict modal
    const handleSubtaskConflictCancel = () => {
      setShowSubtaskConflictModal(false);
      setPendingSubtaskDropData(null);
      setSubtaskConflicts([]);
    };

    // ===== Subtask Out of Range Modal Handlers (subtask moved/resized outside parent) =====

    // Pending data for subtask out of range scenario
    const [pendingSubtaskOutOfRangeDropData, setPendingSubtaskOutOfRangeDropData] = useState<{
      subtaskId: string;
      newStartDate: Date;
      newEndDate: Date;
    } | null>(null);

    // Handler: Extend parent task dates to accommodate subtask
    const handleExtendParent = async () => {
      if (!subtaskOutOfRangeData || !pendingSubtaskOutOfRangeDropData) return;

      setIsUpdatingTask(true);
      try {
        const parentTask = tasksLocalRef.current.find(
          (t) => t.taskId === subtaskOutOfRangeData.parentTaskId,
        );

        if (!parentTask) return;

        const parentStart = parseTaskDate(parentTask.startTime);
        const parentEnd = parseTaskDate(parentTask.endTime);
        const { newStartDate: subtaskNewStart, newEndDate: subtaskNewEnd } = pendingSubtaskOutOfRangeDropData;

        if (!parentStart || !parentEnd) return;

        // Calculate new parent dates
        let newParentStart = parentStart;
        let newParentEnd = parentEnd;

        if (subtaskOutOfRangeData.conflictType === 'before_start' || subtaskOutOfRangeData.conflictType === 'both') {
          newParentStart = subtaskNewStart;
        }
        if (subtaskOutOfRangeData.conflictType === 'after_end' || subtaskOutOfRangeData.conflictType === 'both') {
          newParentEnd = subtaskNewEnd;
        }

        // Update parent task dates
        await updateTaskDates(subtaskOutOfRangeData.parentTaskId, newParentStart, newParentEnd);

        // Update subtask dates
        await updateTaskDates(
          pendingSubtaskOutOfRangeDropData.subtaskId,
          pendingSubtaskOutOfRangeDropData.newStartDate,
          pendingSubtaskOutOfRangeDropData.newEndDate,
        );

        dispatch(
          appendActionMessage({
            message: tSchedule.parentExtendedSubtaskUpdated,
            type: SnackType.SUCCESS,
          }),
        );

        // Reload tasks
        getTasks(true, projectId);
      } catch (e: any) {
        dispatch(
          appendActionMessage({
            message: e?.response?.errors[0]?.message || localeCommon['requestError'],
            type: SnackType.ERROR,
          }),
        );
      } finally {
        setIsUpdatingTask(false);
        setShowSubtaskOutOfRangeModal(false);
        setSubtaskOutOfRangeData(null);
        setPendingSubtaskOutOfRangeDropData(null);
      }
    };

    // Handler: Leave subtask outside parent range
    const handleLeaveSubtaskOutside = async () => {
      if (!pendingSubtaskOutOfRangeDropData) return;

      // Only update subtask, leave parent as is
      // Pass skipParentDateAdjustment flag to prevent backend from auto-adjusting parent dates
      await updateTaskDates(
        pendingSubtaskOutOfRangeDropData.subtaskId,
        pendingSubtaskOutOfRangeDropData.newStartDate,
        pendingSubtaskOutOfRangeDropData.newEndDate,
        { skipParentDateAdjustment: true },
      );

      setShowSubtaskOutOfRangeModal(false);
      setSubtaskOutOfRangeData(null);
      setPendingSubtaskOutOfRangeDropData(null);
    };

    // Handler: Cancel subtask out of range modal
    const handleSubtaskOutOfRangeCancel = () => {
      setShowSubtaskOutOfRangeModal(false);
      setSubtaskOutOfRangeData(null);
      setPendingSubtaskOutOfRangeDropData(null);
    };

    useImperativeHandle(ref, () => ({
      toggleProject,
      toggleMonth,
      toggleView,
      toggleTaskList,
      navigateToDate: handleNavigateToDate,
    }));

    // Initial data loading and filter refreshes
    useEffect(() => {
      if (projectId) {
        getTasks(true, projectId);
        getEvents(true, projectId);
      }
      if (status === 'AwaitingApproval') {
        setShowTaskList(true);
      }
    // getTasks/getEvents are re-created every render and each closes over another local
    // helper, so listing them would refetch the whole schedule on every render. This is
    // meant to fire when the filters change, which is what the array says.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkedAccept, checkedCancel, checkedOnGoing, flag]);

    // Load data when showTaskList is activated
    useEffect(() => {
      if (showTaskList && projectId) {
        getTasks(true, projectId);
        getEvents(true, projectId);
      }
    // Same as above: fires when the task list is opened for a project, not on every
    // render that happens to rebuild the fetchers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showTaskList, projectId]);

    // Organize tasks in tree structure
    useEffect(() => {
      const organizeTasks = (tasks: Task[]): Task[] => {
        const taskMap: Record<string, Task> = {};
        tasks.forEach((task) => {
          if (task.taskId) taskMap[task.taskId] = { ...task, children: [] };
        });
        const root: Task[] = [];
        tasks.forEach((task) => {
          if (task.parentTaskId && taskMap[task.parentTaskId]) {
            taskMap[task.parentTaskId].children!.push(taskMap[task.taskId!]);
          } else {
            if (task.taskId) root.push(taskMap[task.taskId]);
          }
        });
        return root;
      };
      setOrganizedTasks(organizeTasks(tasksLocal));
    }, [tasksLocal]);

    // GraphQL requests
    const getTasks = async (
      initialize = false,
      projectId: string | undefined,
    ) => {
      try {
        const data: getTaskRequest = await graphQlClient.request(
          TasksGraphQL.queries.getTasks,
          { projectId },
        );
        setTasksLocal(data.getTasks);
        setTasksLoaded(true);
        // Fetch budget requests for all tasks
        if (data.getTasks && data.getTasks.length > 0) {
          await fetchAllTaskBudgetRequests(data.getTasks);
        }
      } catch (e: any) {
        // A failed first fetch must not leave the schedule on its skeleton forever.
        setTasksLoaded(true);
        dispatch(
          appendActionMessage({
            message:
              e?.response?.errors[0]?.message || localeCommon['requestError'],
            type: SnackType.ERROR,
          }),
        );
      } finally {
      }
    };

    const getEvents = async (
      initialize = false,
      projectId: string | undefined,
    ) => {
      try {
        const data: getEventRequest = await graphQlClient.request(
          EventsGraphQL.queries.getEvents,
          { projectId },
        );
        setEventsLocal(data.getEvents);
      } catch (e: any) {
        dispatch(
          appendActionMessage({
            message:
              e?.response?.errors[0]?.message || localeCommon['requestError'],
            type: SnackType.ERROR,
          }),
        );
      } finally {
      }
    };

    // Helper function to flatten organized tasks for events (while maintaining references to children)
    const flattenTasksForEvents = (tasks: Task[]): any[] => {
      const result: any[] = [];

      const addTask = (task: Task) => {
        result.push({
          id: task.taskId || 'unknown-task-id',
          crews: task.crews,
          name: task.name,
          startTime: parseTaskDate(task.startTime) || new Date(),
          endTime: parseTaskDate(task.endTime) || new Date(),
          isEvent: false,
          eventType: '',
          status: task.status,
          presedentTaskId: task.presedentTaskId,
          unassigned: task.unassigned,
          children: task.children,
          superintendentId: task.superintendentId,
          budget: task.budget,
          delegateOrganizationId: task.delegateOrganizationId,
          // Delegation links, so the table can tell whose decision an approval is. Without them the
          // table's approve check read `originalOrganizationId` as always undefined.
          delegateTaskId: task.delegateTaskId,
          originalOrganizationId: task.originalOrganizationId,
          parentTaskId: task.parentTaskId,
        });

        // Add children recursively
        if (task.children && task.children.length > 0) {
          task.children.forEach(addTask);
        }
      };

      tasks.forEach(addTask);
      return result;
    };

    const combinedEventArray = [
      ...eventsLocal.map((event) => ({
        id: event.eventId || 'unknown-event-id',
        name: event.name,
        startTime: parseTaskDate(event.eventTime) || new Date(),
        endTime: parseTaskDate(event.eventTime) || new Date(),
        isEvent: true,
        eventType: event.eventType,
      })),
      ...flattenTasksForEvents(organizedTasks),
    ];

    // Use organizedTasks with proper flattening to maintain parent-child hierarchy for timeline resources
    const combinedArray = [
      ...eventsLocal.map((event) => ({
        id: event.eventId || 'unknown-event-id',
        name: event.name,
        startTime: parseTaskDate(event.eventTime) || new Date(),
        endTime: parseTaskDate(event.eventTime) || new Date(),
        isEvent: true,
        eventType: event.eventType,
      })),
      // Use the flattened task structure that maintains parent references
      ...flattenTasksForEvents(organizedTasks),
    ];

    // Task ordering: by start date, constrained by the precedence chain, so a task never
    // renders above one of its precedents. Parent-child grouping is handled separately
    // via parentId on the resource. Dependencies are ALSO surfaced via a badge on the
    // row label, but that badge is informational — the rows themselves are ordered.
    const tasksFlat: any[] = combinedArray.filter((item) => !item.isEvent);

    const taskNameById = new Map<string, string>(
      tasksFlat.map((t: any) => [t.id, t.name]),
    );

    // Set of task IDs that exist in the loaded data. Used to validate
    // parentTaskId before linking a resource to a parent — a missing parent
    // (deleted, soft-deleted, out of scope) would otherwise leave the subtask
    // pointing at a phantom resource and FullCalendar would silently drop the
    // nesting.
    const taskIdsInScope = new Set<string>(tasksFlat.map((t: any) => t.id));

    // Map by id used to walk parent chain when computing depth.
    const itemById = new Map<string, any>(
      tasksFlat.map((t: any) => [t.id, t]),
    );

    // A precedent that finishes after its dependent starts is a scheduling
    // contradiction. Surface it on the row itself — otherwise it stays buried
    // in the task's Dependencies tab and nobody sees it from the schedule.
    // Dates here are already parsed Date objects (see flattenTasksForEvents).
    const precedentWarningById = new Map<string, string>();
    tasksFlat.forEach((task: any) => {
      const startMs =
        task.startTime instanceof Date ? task.startTime.getTime() : NaN;
      if (Number.isNaN(startMs)) return;

      const late = (task.presedentTaskId ?? [])
        .map((precedentId: string) => itemById.get(precedentId))
        .filter(
          (precedent: any) =>
            precedent?.endTime instanceof Date &&
            precedent.endTime.getTime() > startMs,
        )
        .map((precedent: any) => ({
          type: 'task' as const,
          id: precedent.id,
          name: precedent.name,
          endDate: precedent.endTime.toISOString().split('T')[0],
          conflictReason:
            'This task must complete before the current task can start.',
        }));

      if (late.length > 0) {
        precedentWarningById.set(task.id, describePrecedentConflicts(late));
      }
    });

    const computeTaskDepth = (id: string): number => {
      let depth = 0;
      let cursor = itemById.get(id);
      const seen = new Set<string>();
      while (
        cursor?.parentTaskId &&
        !seen.has(cursor.parentTaskId) &&
        taskIdsInScope.has(cursor.parentTaskId)
      ) {
        seen.add(cursor.parentTaskId);
        depth++;
        cursor = itemById.get(cursor.parentTaskId);
        if (!cursor) break;
      }
      return depth;
    };

    // Tasks decorated with their natural index and parsed start timestamp.
    const decoratedTasks = tasksFlat.map((task: any, idx: number) => ({
      ...task,
      originalIdx: idx,
      startMs: task.startTime ? new Date(task.startTime).getTime() : 0,
    }));

    // Row order: ascending by start date, but never placing a task above one of its
    // precedents. startTime is a date (day granularity), so a same-day checklist is
    // entirely ties — sorting on the date alone would leave those rows arbitrary, and
    // the precedence chain is the only record of their real order.
    const reordered = sortBySequence(decoratedTasks).map((task, sortIdx) => ({
      ...task,
      sortOrder: sortIdx,
    }));

    // Create resources for FullCalendar timeline from flat array structure
    const createResourcesForCalendar = (items: any[]): Resource[] => {
      const result: Resource[] = [];

      items.forEach((item) => {
        // A subtask is "linkable" only if its parent is also in the loaded set.
        // Otherwise the parent reference is a phantom and FullCalendar would
        // fail to nest the row.
        const hasLinkableParent =
          !item.isEvent &&
          !!item.parentTaskId &&
          taskIdsInScope.has(item.parentTaskId);

        let title = item.isEvent ? item?.eventType : item.name;

        // Add the ↳ prefix and depth-based indent when the task is actually
        // nested under its parent. The non-breaking spaces add visible
        // indentation per nesting level so subtasks appear clearly below
        // their parent (FullCalendar's native indent is too subtle on its
        // own). If the parent is missing from scope the task falls back to
        // root level and the arrow would be misleading.
        const depth = !item.isEvent ? computeTaskDepth(item.id) : 0;
        const indentPrefix = depth > 0 ? '    '.repeat(depth) : '';

        if (hasLinkableParent) {
          title = `${indentPrefix}↳ ${item.name}`;
        }

        // Add original organization info for delegated tasks
        if (
          !item.isEvent &&
          item.originalTaskId &&
          item.originalOrganization?.name
        ) {
          const prefix = hasLinkableParent ? `${indentPrefix}↳ ` : '';
          title = `${prefix}${item.name} (from: ${item.originalOrganization.name})`;
        }

        // Parse dates to ensure they are valid Date objects
        const startDate = parseTaskDate(item.startTime);
        const endDate = parseTaskDate(item.endTime);

        const presedentTaskNames: string[] =
          !item.isEvent && Array.isArray(item.presedentTaskId)
            ? item.presedentTaskId
                .map((depId: string) => taskNameById.get(depId))
                .filter((name: string | undefined): name is string => !!name)
            : [];

        const resource: Resource = {
          id: item.isEvent ? item?.eventType : item.id,
          title,
          start: startDate ? getCalendarDateString(startDate) : '',
          end: endDate ? getCalendarDateString(endDate) : '',
          isEvent: item.isEvent,
          sortOrder: item.isEvent
            ? 0
            : reordered.find((task: any) => task.id === item.id)?.sortOrder,
          superintendentId: item.superintendentId,
          presedentTaskNames,
          precedentWarning: item.isEvent
            ? ''
            : precedentWarningById.get(item.id) || '',
        } as Resource;

        // Add parent reference only when the parent is actually in scope —
        // otherwise FullCalendar would try to nest under a phantom and drop
        // the row from the tree.
        if (hasLinkableParent) {
          (resource as any).parentId = item.parentTaskId;
        }

        result.push(resource);
      });

      return result;
    };

    // Memoized on the three states the whole derivation above is a pure function of.
    // Without this, `myResources` gets a fresh identity on every render, which fires the
    // "UPDATE RESOURCES" effect below and makes FullCalendar re-parse and re-sort the
    // entire resource tree constantly — that is what turns any ordering change into
    // visible flicker.
    const myResources: Resource[] = useMemo(
      () => createResourcesForCalendar(combinedArray),
      // `combinedArray` and `createResourcesForCalendar` are rebuilt on every render, so
      // listing them would defeat the memo. Both are pure functions of the two states
      // below, so these deps are complete in practice.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [organizedTasks, eventsLocal],
    );

    // Helper functions for event color logic
    const getEventColor = (item: any): string => {
      if (item.isEvent) {
        return COLORS.eventOrange;
      }

      // Use the unified task status display utility with budget requests
      const budgetRequests = taskBudgetRequests.get(item.id) || [];
      return getTaskStatusDisplay(item, budgetRequests).color;
    };

    const createEventObject = (item: any): Evento => {
      // For FullCalendar, end dates are exclusive, so we add 1 day to make the range inclusive
      const end = addDaysToDate(item.endTime, 1);

      let title = item.name;
      // Add original organization info for delegated tasks
      if (
        !item.isEvent &&
        item.originalTaskId &&
        item.originalOrganization?.name
      ) {
        title = `${item.name} (from: ${item.originalOrganization.name})`;
      }

      return {
        id: item.id,
        title,
        start: toCalendarISOString(item.startTime),
        end: toCalendarISOString(end),
        resourceId: item.isEvent ? item?.eventType : item.id,
        color: getEventColor(item),
        textColor: 'white',
        isEvent: item.isEvent,
      };
    };

    // Final events for calendar
    const myEvents: Evento[] = combinedEventArray.map(createEventObject);

    // Handlers
    const handleTaskDetail = (id: string) => {
      if (setTaskDetails) {
        setTaskDetails(id);
      } else {
        setTask(id);
      }
    };

    const handleEventDetail = (id: string) => {
      setEvent(id, events);
    };

    const toggleView = () => {
      // Go to calendar view
      setCurrentView('calendar');
      setView('dayGridMonth');
      setShowTaskList(false);
    };

    const toggleTaskList = () => {
      // Go to task list
      setCurrentView('taskList');
      setShowTaskList(true);
    };

    // Navigate calendar to a specific date (used when clicking a date in the task list)
    const handleNavigateToDate = (dateValue: string | Date) => {
      const targetDate =
        dateValue instanceof Date ? dateValue : parseTaskDate(dateValue);
      if (!targetDate) return;

      userNavigatedRef.current = true;
      // Clearing the range rebuilds the calendar; make it open on the target.
      pendingInitialDateRef.current = toCalendarISOString(targetDate);

      // Clear any custom date range so the calendar shows the default month view
      setTimelineStartDate(null);
      setTimelineEndDate(null);

      // Switch to timeline view
      setCurrentView('timeline');
      setView('resourceTimelineMonth');
      setShowTaskList(false);

      // Navigate to the target date after the calendar is re-initialized (or
      // directly, when nothing above caused a rebuild).
      setTimeout(() => {
        pendingInitialDateRef.current = null;
        const cal = calendarApiRef.current;
        if (cal) {
          cal.gotoDate(targetDate);
          scrollToDateColumn(toCalendarISOString(targetDate));
          setTimeout(updateCalendarTitle, 10);
        }
      }, 100);
    };

    // Story-209 (D9). Both timeline toggles clear the custom range (issue 189: the old monthly
    // toggle never did) so no range can carry over from one view to the other.
    const showTimelineView = (nextView: string) => {
      setTimelineStartDate(null);
      setTimelineEndDate(null);
      setCurrentView('timeline');
      setView(nextView);
      setShowTaskList(false);
    };

    const toggleProject = () => showTimelineView(PROJECT_VIEW);

    const toggleMonth = () => showTimelineView('resourceTimelineMonth');

    // Fetch budget requests for all tasks
    const fetchAllTaskBudgetRequests = async (tasks: Task[]) => {
      const budgetRequestsMap = new Map<string, any[]>();

      // Fetch budget requests for all tasks in parallel
      const budgetPromises = tasks.map(async (task) => {
        if (task.taskId) {
          try {
            const budgetRequests = await fetchBudgetRequestsByTaskId(
              task.taskId,
            );
            budgetRequestsMap.set(task.taskId, budgetRequests || []);
          } catch (error) {
            console.error(
              `Error fetching budget requests for task ${task.taskId}:`,
              error,
            );
            budgetRequestsMap.set(task.taskId, []);
          }
        }
      });

      await Promise.all(budgetPromises);
      setTaskBudgetRequests(budgetRequestsMap);
    };

    const reloadTasks = () => {
      getTasks(true, projectId);
      getEvents(true, projectId);
    };

    // Generate calendar title based on current state
    const generateCalendarTitle = (): string => {
      // Project view: the range is the title. Month view may also pin a custom range.
      const rangeStart =
        view === PROJECT_VIEW ? projectRangeRef.current?.start : timelineStartDate;
      const rangeEnd =
        view === PROJECT_VIEW ? projectRangeRef.current?.end : timelineEndDate;
      if (rangeStart && rangeEnd) {
        const startDate = parseTaskDate(rangeStart)!;
        const endDate = parseTaskDate(rangeEnd)!;

        const startMonth = startDate.getMonth();
        const endMonth = endDate.getMonth();
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();

        // If same month and year, show just the month name
        if (startMonth === endMonth && startYear === endYear) {
          return startDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
          });
        }

        // If different months, show date range format
        const startDay = startDate.getDate();
        const endDay = endDate.getDate();

        const startMonthName = startDate.toLocaleDateString('en-US', {
          month: 'short',
        });
        const endMonthName = endDate.toLocaleDateString('en-US', {
          month: 'short',
        });

        // If same year, don't repeat the year
        if (startYear === endYear) {
          return `${startDay} ${startMonthName} - ${endDay} ${endMonthName} ${startYear}`;
        } else {
          return `${startDay} ${startMonthName} ${startYear} - ${endDay} ${endMonthName} ${endYear}`;
        }
      }

      // Default title logic
      const cal = calendarApiRef.current;
      if (cal) {
        const currentDate = cal.getDate();
        const viewType = cal.view.type;

        if (viewType === 'resourceTimelineMonth') {
          return currentDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
          });
        } else if (viewType === 'dayGridMonth') {
          return currentDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
          });
        } else {
          return cal.view.title || '';
        }
      }

      return '';
    };

    const updateCalendarTitle = () => {
      setCalendarTitle(generateCalendarTitle());
    };

    // Horizontally centre the timeline on a calendar day (YYYY-MM-DD).
    // FullCalendar stamps every day slot with data-date; `.fc-day-today` is
    // only a fallback because it doesn't exist when today isn't rendered.
    const scrollToDateColumn = (dateStr: string) => {
      const root = calendarRef.current;
      if (!root) return;

      const targetCell = (root.querySelector(
        `.fc-timeline-slot-lane[data-date="${dateStr}"]`,
      ) || root.querySelector('.fc-day-today')) as HTMLElement | null;
      if (!targetCell) return;

      const scrollContainer = targetCell.closest('.fc-scroller') as HTMLElement;
      if (!scrollContainer) return;

      const containerWidth = scrollContainer.clientWidth;
      const cellOffset = targetCell.offsetLeft;
      const cellWidth = targetCell.offsetWidth;

      const scrollPosition = cellOffset - containerWidth / 2 + cellWidth / 2;
      scrollContainer.scrollLeft = Math.max(0, scrollPosition);
    };

    const handlePrevClick = () => {
      userNavigatedRef.current = true;
      const cal = calendarApiRef.current;
      if (cal) {
        cal.prev();
        setTimeout(updateCalendarTitle, 10);
      }
    };

    const handleNextClick = () => {
      userNavigatedRef.current = true;
      const cal = calendarApiRef.current;
      if (cal) {
        cal.next();
        setTimeout(updateCalendarTitle, 10);
      }
    };

    const handleTitleClick = () => {
      userNavigatedRef.current = true;
      const todayStr = toCalendarISOString(new Date());

      // Project view already renders the whole range: Today just scrolls to today's column.
      if (view === PROJECT_VIEW) {
        scrollToDateColumn(todayStr);
        return;
      }

      if (timelineStartDate || timelineEndDate) {
        // Clearing the range rebuilds the calendar; make it open on today.
        pendingInitialDateRef.current = todayStr;
        setTimelineStartDate(null);
        setTimelineEndDate(null);
        return;
      }

      // No range set: the current calendar survives, so navigate it directly.
      const cal = calendarApiRef.current;
      if (cal) {
        cal.today();
        scrollToDateColumn(todayStr);
        setTimeout(updateCalendarTitle, 10);
      }
    };

    // Appends a weather glyph (with its own hover/focus tooltip) into a rendered slot-label or
    // day-cell element, for a cell mounted by FullCalendar's `slotLabelDidMount`/
    // `dayCellDidMount` hooks (below). Delegates to the shared `attachWeatherGlyphByKey` helper
    // (`./weather/applyWeatherGlyphs`) so cells created at DidMount time and cells already on
    // screen when weather data resolves (handled by the `applyWeatherGlyphs` effect above) share
    // identical clear/attach/no-duplicate logic. No-op for a day with no stored value, an
    // UNAVAILABLE marker, or no condition — those render nothing, per the AC. `selectors` are
    // tried in order so this works across the different cushion structures the timeline vs.
    // dayGrid views mount; falls back to the element itself if none match.
    const attachWeatherGlyph = (el: HTMLElement, date: Date, selectors: string[]) => {
      attachWeatherGlyphByKey(el, toCalendarISOString(date), selectors, {
        weatherByDate: weatherByDateRef.current,
        strings: weatherLocaleStrings,
        dateLocale: weatherDateLocale,
      });
    };

    // -------------------
    //   CALENDAR INIT
    // -------------------
    useEffect(() => {
      // Only initialize calendar if we're in timeline or calendar view
      if (currentView === 'timeline' || currentView === 'calendar') {
        if (!calendarRef.current) return;

        // Destroy existing calendar if there is one
        if (calendarApiRef.current) {
          calendarApiRef.current.destroy();
          calendarApiRef.current = null;
        }

        // Project view waits for the first task fetch (no range from empty tasks). The calendar is
        // built by this same effect once `projectReady` flips.
        if (view === PROJECT_VIEW && !projectRangeRef.current) return;

        // Build calendar options
        const calendarOptions: any = {
          plugins: [resourceTimelinePlugin, dayGridPlugin, interactionPlugin],
          schedulerLicenseKey: process.env.REACT_APP_FULLCALENDAR_KEY,
          initialView: view,
          // Always registered (never conditional); its range is read through a ref (see
          // `buildProjectView`). Month has no `visibleRange` at all, so nothing can leak into it.
          views: buildProjectView(() => projectRangeRef.current),
          editable: true,
          selectable: false,
          height: 'auto',
          customButtons: {},
          headerToolbar: false,
          resourcesInitiallyExpanded: true,
          resources: [],
          events: [],
          // --- Weather (Sprint 25, story-133) ---
          // Tracks the actually-rendered date range for the weather query — fires on initial
          // paint, prev/next navigation and view switches alike.
          datesSet: (info: any) => {
            const from = toCalendarISOString(info.view.activeStart);
            // `activeEnd` is exclusive per FullCalendar's own convention.
            const inclusiveEnd = new Date(info.view.activeEnd.getTime() - 24 * 60 * 60 * 1000);
            const to = toCalendarISOString(inclusiveEnd);
            // The Project view can span years; bound the weather read (D10). Month is unchanged.
            const next = info.view.type === PROJECT_VIEW ? clampWeatherRange(from, to) : { from, to };
            setWeatherVisibleRange((prev) =>
              prev === next || (prev && next && prev.from === next.from && prev.to === next.to) ? prev : next,
            );
          },
          // DOM-append (DidMount), not content-replacement (Content), for both hooks below —
          // the UX doc's own documented fallback ("an equivalent is a dayCellDidMount that
          // appends the glyph node; the visible result must be the same") applied to both, since
          // this calendar's slot-label/day-cell markup is deeply version- and setup-specific
          // (two-row Project view format, no navLinks, etc.) and reproducing FullCalendar's
          // own default content here would risk silently breaking it. Both callbacks read
          // `weatherByDateRef.current`, never a value captured by this effect's own closure — but
          // they only fire for a cell at the moment FullCalendar creates it. `Calendar#render()`
          // does NOT re-invoke DidMount on cells already mounted (sprint 34 regression fix), so a
          // cell that existed before weather data resolved is instead repainted by the
          // `applyWeatherGlyphs` effect above, which walks the already-mounted DOM directly.
          slotLabelDidMount: (arg: any) => {
            // Only the bottom/date-bearing label row. The custom two-row Project view
            // format (month/year top, day/weekday bottom, see `buildProjectView`) is the only multi-row
            // format in this file — level 0 there is the month/year row, which never gets a
            // weather glyph.
            if (view === PROJECT_VIEW && arg.level === 0) return;
            attachWeatherGlyph(arg.el, arg.date, [
              '.fc-col-header-cell-cushion',
              '.fc-timeline-slot-cushion',
              '.fc-timeline-slot-frame',
            ]);
          },
          dayCellDidMount: (arg: any) => {
            attachWeatherGlyph(arg.el, arg.date, ['.fc-daygrid-day-top']);
          },
        };

        // Open on: the project range start (Project view) > the one-shot target set by the Today
        // button / task-list date links > the project-aware anchor. Never on FullCalendar's
        // default of "now".
        if (view === PROJECT_VIEW && projectRangeRef.current) {
          calendarOptions.initialDate = projectRangeRef.current.start;
          appliedProjectRangeRef.current = `${projectRangeRef.current.start}|${projectRangeRef.current.end}`;
        } else if (pendingInitialDateRef.current) {
          calendarOptions.initialDate = pendingInitialDateRef.current;
        } else {
          calendarOptions.initialDate = anchorDateRef.current;
        }
        pendingInitialDateRef.current = null;

        // Month view only: a custom range pinned through the header date pickers. Project view
        // owns its own range inside its view definition and never reads these.
        if (view !== PROJECT_VIEW && timelineStartDate && timelineEndDate) {
          calendarOptions.visibleRange = {
            start: timelineStartDate,
            end: timelineEndDate,
          };
        }

        const calendar = new Calendar(calendarRef.current, {
          ...calendarOptions,
          eventDragStart: function (info) {
            setIsDragging(true);
            setDraggingTaskName(info.event.title);
          },
          eventDragStop: function (info) {
            setIsDragging(false);
            setDraggingTaskName('');
          },
          eventDrop: async function (info) {
            if (
              info.oldResource?._resource.id !== info.newResource?._resource.id
            ) {
              info.revert();
              return;
            }

            // Get new dates from the drop
            const newStartDate = info.event.start;
            const newEndDate = info.event.end;
            const taskId = info.event.id;

            if (!newStartDate) return;

            // FullCalendar end dates are exclusive, so subtract 1 day to get actual end date
            const actualEndDate = newEndDate
              ? addDaysToDate(newEndDate, -1)
              : newStartDate;

            // Get the task being moved
            const movedTask = tasksLocalRef.current.find((t) => t.taskId === taskId);

            // Check if task is delegated - show warning first
            const delegatedTask = checkDelegatedTask(taskId, tasksLocalRef.current);
            if (delegatedTask) {
              setDelegatedTaskData(delegatedTask);
              setPendingDelegatedDropData({
                taskId,
                newStartDate,
                newEndDate: actualEndDate,
              });
              setShowDelegatedTaskModal(true);
              info.revert();
              return;
            }

            // Check for precedent conflicts before proceeding
            const precedentConflictsList = checkPrecedentConflicts(
              taskId,
              newStartDate,
              tasksLocalRef.current,
              eventsLocalRef.current,
            );

            if (precedentConflictsList.length > 0) {
              setPendingDropData({
                taskId,
                newStartDate,
                newEndDate: actualEndDate,
              });
              setPrecedentConflicts(precedentConflictsList);
              setShowPrecedentConflictModal(true);
              info.revert();
              return;
            }

            // Check if this task is a subtask being moved outside its parent's range
            const subtaskOutOfRange = checkSubtaskOutOfParentRange(
              taskId,
              newStartDate,
              actualEndDate,
              tasksLocalRef.current,
            );

            if (subtaskOutOfRange) {
              setSubtaskOutOfRangeData(subtaskOutOfRange);
              setPendingSubtaskOutOfRangeDropData({
                subtaskId: taskId,
                newStartDate,
                newEndDate: actualEndDate,
              });
              setSubtaskOutOfRangeMode('move');
              setShowSubtaskOutOfRangeModal(true);
              info.revert();
              return;
            }

            // Check if this parent task move will leave subtasks outside the new range
            const subtaskConflictsList = checkSubtaskConflicts(
              taskId,
              newStartDate,
              actualEndDate,
              tasksLocalRef.current,
            );

            if (subtaskConflictsList.length > 0) {
              // Get the old start date from the task
              const oldStartDate = movedTask?.startTime
                ? parseTaskDate(movedTask.startTime) || new Date()
                : new Date();

              // Get all subtasks that need to be moved
              const subtasksToMove = tasksLocalRef.current.filter(
                (t) => t.parentTaskId === taskId,
              );

              setSubtaskConflicts(subtaskConflictsList);
              setPendingSubtaskDropData({
                taskId,
                newStartDate,
                newEndDate: actualEndDate,
                oldStartDate,
                subtasksToMove,
              });
              setSubtaskConflictMode('move');
              setShowSubtaskConflictModal(true);
              info.revert();
              return;
            }

            // No conflicts - update task dates directly
            const success = await updateTaskDates(taskId, newStartDate, actualEndDate);

            if (!success) {
              info.revert();
            }
          },
          // Resize visual feedback
          eventResizeStart: function (info) {
            setIsDragging(true);
            setDraggingTaskName(info.event.title);
          },
          eventResizeStop: function (info) {
            setIsDragging(false);
            setDraggingTaskName('');
          },
          // Handle task resize (extend/contract duration)
          eventResize: async function (info) {
            // Get new dates from the resize
            const newStartDate = info.event.start;
            const newEndDate = info.event.end;
            const taskId = info.event.id;

            if (!newStartDate) return;

            // FullCalendar end dates are exclusive, so subtract 1 day
            const actualEndDate = newEndDate
              ? addDaysToDate(newEndDate, -1)
              : newStartDate;

            // Get the task being resized
            const resizedTask = tasksLocalRef.current.find((t) => t.taskId === taskId);

            // Check if task is delegated - show warning first
            const delegatedTask = checkDelegatedTask(taskId, tasksLocalRef.current);
            if (delegatedTask) {
              setDelegatedTaskData(delegatedTask);
              setPendingDelegatedDropData({
                taskId,
                newStartDate,
                newEndDate: actualEndDate,
              });
              setShowDelegatedTaskModal(true);
              info.revert();
              return;
            }

            // Check for precedent conflicts before proceeding
            const precedentConflictsList = checkPrecedentConflicts(
              taskId,
              newStartDate,
              tasksLocalRef.current,
              eventsLocalRef.current,
            );

            if (precedentConflictsList.length > 0) {
              setPendingDropData({
                taskId,
                newStartDate,
                newEndDate: actualEndDate,
              });
              setPrecedentConflicts(precedentConflictsList);
              setShowPrecedentConflictModal(true);
              info.revert();
              return;
            }

            // Check if this task is a subtask being resized outside its parent's range
            const subtaskOutOfRange = checkSubtaskOutOfParentRange(
              taskId,
              newStartDate,
              actualEndDate,
              tasksLocalRef.current,
            );

            if (subtaskOutOfRange) {
              setSubtaskOutOfRangeData(subtaskOutOfRange);
              setPendingSubtaskOutOfRangeDropData({
                subtaskId: taskId,
                newStartDate,
                newEndDate: actualEndDate,
              });
              setSubtaskOutOfRangeMode('resize');
              setShowSubtaskOutOfRangeModal(true);
              info.revert();
              return;
            }

            // Check if this parent task resize will leave subtasks outside the new range
            const subtaskConflictsList = checkSubtaskConflicts(
              taskId,
              newStartDate,
              actualEndDate,
              tasksLocalRef.current,
            );

            if (subtaskConflictsList.length > 0) {
              // Get the old start date from the task
              const oldStartDate = resizedTask?.startTime
                ? parseTaskDate(resizedTask.startTime) || new Date()
                : new Date();

              // Get all subtasks that need to be adjusted
              const subtasksToMove = tasksLocalRef.current.filter(
                (t) => t.parentTaskId === taskId,
              );

              setSubtaskConflicts(subtaskConflictsList);
              setPendingSubtaskDropData({
                taskId,
                newStartDate,
                newEndDate: actualEndDate,
                oldStartDate,
                subtasksToMove,
              });
              setSubtaskConflictMode('resize');
              setShowSubtaskConflictModal(true);
              info.revert();
              return;
            }

            // No conflicts - update task dates directly
            const success = await updateTaskDates(
              taskId,
              newStartDate,
              actualEndDate,
            );

            if (!success) {
              info.revert();
            }
          },
          eventContent: (args) => {
            if (view === 'resourceTimelineMonth') {
              const resourceId = args.event._def?.resourceIds?.[0] ?? '';
              const icons: Record<string, string> = {
                Incident: '⚠️',
                Inspection: '📋',
                Visitor: '👤',
                Permit: '🔐',
                Communication: 'ℹ️',
              };
              return {
                html: `<span class="icon-container">${icons[resourceId] || '&nbsp;'}</span>`,
              };
            }
            return true;
          },
          resourceAreaColumns: [
            {
              field: 'title',
              headerContent: 'Tasks',
              width: 60,
            },
            {
              field: 'startDate',
              headerContent: 'Start',
              width: 30,
              cellContent: (args) => {
                const isEvent = args.resource?.extendedProps?.isEvent;
                if (isEvent) return '⚠️';
                const dateStr = args.resource?.extendedProps?.start;
                const display = dateStr
                  ? formatTaskDateForDisplay(parseTaskDate(dateStr))
                  : 'No Start Date';
                return { html: `<span class="date-navigate" data-date="${dateStr || ''}" style="cursor:pointer;" title="Click to navigate calendar to this date">${display}</span>` };
              },
              cellDidMount: (args: any) => {
                const el = args.el.querySelector('.date-navigate');
                if (el) {
                  el.addEventListener('click', (e: any) => {
                    e.stopPropagation();
                    const dateStr = el.getAttribute('data-date');
                    if (dateStr) {
                      const targetDate = parseTaskDate(dateStr);
                      if (targetDate && calendarApiRef.current) {
                        calendarApiRef.current.gotoDate(targetDate);
                        setTimeout(updateCalendarTitle, 10);
                      }
                    }
                  });
                }
              },
            },
            {
              field: 'endDate',
              headerContent: 'End',
              width: 30,
              cellContent: (args: any) => {
                const isEvent = args.resource?.extendedProps?.isEvent;
                if (isEvent) return '';
                const dateStr = args.resource?.extendedProps?.end;
                const display = dateStr
                  ? formatTaskDateForDisplay(parseTaskDate(dateStr))
                  : 'No End Date';
                return { html: `<span class="date-navigate" data-date="${dateStr || ''}" style="cursor:pointer;" title="Click to navigate calendar to this date">${display}</span>` };
              },
              cellDidMount: (args: any) => {
                const el = args.el.querySelector('.date-navigate');
                if (el) {
                  el.addEventListener('click', (e: any) => {
                    e.stopPropagation();
                    const dateStr = el.getAttribute('data-date');
                    if (dateStr) {
                      const targetDate = parseTaskDate(dateStr);
                      if (targetDate && calendarApiRef.current) {
                        calendarApiRef.current.gotoDate(targetDate);
                        setTimeout(updateCalendarTitle, 10);
                      }
                    }
                  });
                }
              },
            },
            {
              field: 'Days',
              headerContent: 'Days',
              width: 20,
              cellContent: (args) => {
                const endDate = parseTaskDate(
                  args.resource?.extendedProps?.end,
                );
                const startDate = parseTaskDate(
                  args.resource?.extendedProps?.start,
                );
                if (!endDate || !startDate) {
                  return args.resource?.extendedProps?.isEvent
                    ? ''
                    : 'No End Date';
                }
                const differenceInDays = calculateDaysDifference(
                  startDate,
                  endDate,
                );
                return args.resource?.extendedProps?.isEvent
                  ? ''
                  : differenceInDays;
              },
            },
          ],
          resourceOrder: '-isEvent, sortOrder, start',
          eventClick: (info) => {
            if (info.event.id && info.event._def.resourceIds) {
              if (
                info.event._def.resourceIds[0] === 'Incident' ||
                info.event._def.resourceIds[0] === 'Inspection' ||
                info.event._def.resourceIds[0] === 'Visitor' ||
                info.event._def.resourceIds[0] === 'Permit' ||
                info.event._def.resourceIds[0] === 'Communication' ||
                info.event._def.resourceIds[0] === 'Other'
              ) {
                setEvent(info.event.id, events);
              } else {
                setTask(info.event.id);
              }
            }
          },
          resourceLabelContent: (arg) => {
            const title = arg.resource.title.toLowerCase();
            if (
              [
                'incident',
                'inspection',
                'visitor',
                'permit',
                'communication',
                'other',
              ].includes(title)
            ) {
              return {
                html: `<span class="resource-incident">${arg.resource.title}</span>`,
              };
            }

            const escapeHtml = (s: string) =>
              s
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');

            const depNames: string[] =
              (arg.resource.extendedProps as any)?.presedentTaskNames || [];

            // Amber triangle for a precedent scheduled after this task —
            // deliberately the same shape as the red indicators elsewhere, in
            // the warning colour because it never blocks anything.
            const precedentWarning: string =
              (arg.resource.extendedProps as any)?.precedentWarning || '';
            const warningBadge = precedentWarning
              ? `<span class="resource-precedent-warning" title="${escapeHtml(
                  precedentWarning,
                )}" aria-label="${escapeHtml(precedentWarning)}">` +
                `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>` +
                `</span>`
              : '';

            if (depNames.length === 0) {
              return warningBadge
                ? {
                    html: `<span class="resource-dep-row"><span class="resource-dep-row__title">${escapeHtml(
                      arg.resource.title,
                    )}</span>${warningBadge}</span>`,
                  }
                : { html: escapeHtml(arg.resource.title) };
            }

            // Minimal dependency indicator: tiny icon to the right edge of the
            // cell. Slate-500 for a single dep, indigo-600 + count when more.
            // No pill, no background — keeps the row clean and the task name
            // dominant. Subtask hierarchy is communicated by FullCalendar's
            // tree indent alone (Gantt convention).
            const linkIconSvg = `<svg class="resource-dep-badge__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>`;

            const isMulti = depNames.length > 1;
            const countLabel = isMulti
              ? `<span class="resource-dep-badge__count">${depNames.length}</span>`
              : '';

            const depsAttr = escapeHtml(JSON.stringify(depNames));
            const badge = `<span class="resource-dep-badge${
              isMulti ? ' resource-dep-badge--multi' : ''
            }" data-deps="${depsAttr}" tabindex="0" aria-label="Depends on ${
              depNames.length
            } task${isMulti ? 's' : ''}">${linkIconSvg}${countLabel}</span>`;

            return {
              html: `<span class="resource-dep-row"><span class="resource-dep-row__title">${escapeHtml(
                arg.resource.title,
              )}</span>${warningBadge}${badge}</span>`,
            };
          },
          resourceLabelDidMount: (arg) => {
            // Add click handler to resource label (task name)
            const labelEl = arg.el;
            const resourceId = arg.resource.id;
            const isEvent = arg.resource.extendedProps?.isEvent;

            // Make the label clickable with cursor pointer
            labelEl.style.cursor = 'pointer';

            // Add click event listener
            labelEl.addEventListener('click', (e: MouseEvent) => {
              // Ignore clicks on the expand/collapse toggle button
              const target = e.target as HTMLElement;
              if (target.closest('.fc-datagrid-expander')) {
                return;
              }

              // Don't navigate if the click started on the dependency badge
              if (target.closest('.resource-dep-badge')) {
                return;
              }

              if (isEvent) {
                // If it's an event, open event modal
                handleEventDetail(resourceId);
              } else {
                // If it's a task, open CreateEditTaskModal
                setTask(resourceId);
              }
            });

            // Dependency-badge popover wiring.
            //
            // Event delegation on labelEl (instead of attaching directly to the
            // badge span): when FullCalendar re-renders the cell content (e.g.
            // after a drag/resize/refetch), it replaces the inner HTML and any
            // listeners attached to the badge span are lost. labelEl persists
            // across re-renders, so listeners attached here keep working.
            // mouseover/mouseout bubble (mouseenter/mouseleave do not), which
            // is what makes delegation possible.
            let activeBadge: HTMLElement | null = null;
            labelEl.addEventListener('mouseover', (e: MouseEvent) => {
              const badge = (e.target as HTMLElement | null)?.closest(
                '.resource-dep-badge',
              ) as HTMLElement | null;
              if (!badge || badge === activeBadge) return;
              activeBadge = badge;
              showDepPopover(badge);
            });
            labelEl.addEventListener('mouseout', (e: MouseEvent) => {
              if (!activeBadge) return;
              const next = e.relatedTarget as HTMLElement | null;
              if (next && activeBadge.contains(next)) return;
              activeBadge = null;
              hideDepPopover();
            });
            labelEl.addEventListener('focusin', (e: FocusEvent) => {
              const badge = (e.target as HTMLElement | null)?.closest(
                '.resource-dep-badge',
              ) as HTMLElement | null;
              if (!badge) return;
              activeBadge = badge;
              showDepPopover(badge);
            });
            labelEl.addEventListener('focusout', () => {
              activeBadge = null;
              hideDepPopover();
            });
            // Hide on any scroll within the schedule, so the popover never
            // "floats" out of place if the user scrolls while hovering.
            const scrollContainer = labelEl.closest('.fc-scroller');
            scrollContainer?.addEventListener(
              'scroll',
              () => {
                activeBadge = null;
                hideDepPopover();
              },
              { passive: true },
            );
          },
        });

        calendar.render();
        calendarApiRef.current = calendar;

        // Update the title after calendar is rendered
        setTimeout(updateCalendarTitle, 10);

        // Centre the timeline on the day it opened on
        setTimeout(() => {
          if (view === PROJECT_VIEW && projectRangeRef.current) {
            // First mount of Project view: today if it is inside the range, else the range start.
            const todayStr = toCalendarISOString(new Date());
            const { start, end } = projectRangeRef.current;
            scrollToDateColumn(todayStr >= start && todayStr <= end ? todayStr : start);
          } else {
            scrollToDateColumn(toCalendarISOString(calendar.getDate()));
          }
        }, 100);
      } else {
        // If we're in task list, destroy the calendar
        if (calendarApiRef.current) {
          calendarApiRef.current.destroy();
          calendarApiRef.current = null;
        }
      }

      return () => {
        if (calendarApiRef.current) {
          calendarApiRef.current.destroy();
          calendarApiRef.current = null;
        }
      };
    // Rebuilds the calendar when the view or its range changes. The handlers it reaches
    // for are re-created every render; depending on them would tear down and rebuild
    // FullCalendar continuously.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentView, view, timelineStartDate, timelineEndDate, projectReady]); // Updated dependencies

    // -------------------
    // UPDATE VISIBLE RANGE (Month view's custom range only)
    // -------------------
    useEffect(() => {
      const cal = calendarApiRef.current;
      if (!cal || currentView === 'taskList' || view === PROJECT_VIEW) return;

      if (timelineStartDate && timelineEndDate) {
        // Add one day to end date to make it inclusive (FullCalendar end dates are exclusive)
        const endDateString = toCalendarISOString(
          addDaysToDate(parseTaskDate(timelineEndDate)!, 1),
        );
        cal.setOption('visibleRange', {
          start: timelineStartDate,
          end: endDateString,
        });
        cal.gotoDate(timelineStartDate);
      } else {
        cal.setOption('visibleRange', undefined);
      }
    }, [timelineStartDate, timelineEndDate, currentView, view]);

    // -------------------
    // PROJECT RANGE FOLLOWS THE TASKS
    // -------------------
    // Single place a task drag/resize/create/delete (all of which end in `setTasksLocal`) reaches
    // the Project view. String deps: an unchanged range does nothing. The value is read by the
    // view's `visibleRange` function, so re-evaluating it is `gotoDate(getDate())` (verified
    // against FullCalendar 6.1.20; `setOption('views', ...)` does not re-run the range).
    const projectRangeKey = projectRange ? `${projectRange.start}|${projectRange.end}` : null;
    useEffect(() => {
      const cal = calendarApiRef.current;
      if (!cal || view !== PROJECT_VIEW || currentView !== 'timeline' || !projectRangeKey) return;
      if (appliedProjectRangeRef.current === projectRangeKey) return;
      appliedProjectRangeRef.current = projectRangeKey;

      const scroller = calendarRef.current
        ?.querySelector('.fc-timeline-slot-lane')
        ?.closest('.fc-scroller') as HTMLElement | null | undefined;
      const scrollLeft = scroller?.scrollLeft ?? 0;

      cal.gotoDate(cal.getDate());
      updateCalendarTitle();

      // Keep the user where they were; the browser clamps if the range shrank.
      requestAnimationFrame(() => {
        const next = calendarRef.current
          ?.querySelector('.fc-timeline-slot-lane')
          ?.closest('.fc-scroller') as HTMLElement | null | undefined;
        if (next) next.scrollLeft = scrollLeft;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectRangeKey, view, currentView]);

    // -------------------
    //   ANCHOR DATE
    // -------------------
    // Tasks load after the calendar is built, and the behind-schedule rule
    // needs them. Re-anchor once they (or the project dates) arrive, unless
    // the user has already navigated or pinned a custom range.
    useEffect(() => {
      const anchor = getScheduleAnchorDate({
        projectStartDate,
        projectEndDate,
        tasks: tasksLocal,
      });
      const changed = anchor !== anchorDateRef.current;
      anchorDateRef.current = anchor;

      const cal = calendarApiRef.current;
      if (
        !changed ||
        !cal ||
        userNavigatedRef.current ||
        currentView === 'taskList' ||
        view === PROJECT_VIEW ||
        timelineStartDate ||
        timelineEndDate
      ) {
        return;
      }

      cal.gotoDate(anchor);
      scrollToDateColumn(anchor);
      updateCalendarTitle();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tasksLocal, projectStartDate, projectEndDate]);

    // -------------------
    // UPDATE TITLE WHEN DATE RANGE CHANGES
    // -------------------
    useEffect(() => {
      updateCalendarTitle();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timelineStartDate, timelineEndDate]);

    // -------------------
    // SYNCHRONIZE DATA WITH PROPS
    // -------------------
    useEffect(() => {
      // Synchronize local events with props when they change
      if (events) {
        setEventsLocal(events);
      }
    }, [events]);

    useEffect(() => {
      // Synchronize local tasks with props when they change
      if (tasks) {
        setTasksLocal(tasks);
        setTasksLoaded(true);
      }
    }, [tasks]);

    // Keep refs in sync with state (for use in event handlers to avoid stale closures)
    useEffect(() => {
      tasksLocalRef.current = tasksLocal;
    }, [tasksLocal]);

    useEffect(() => {
      eventsLocalRef.current = eventsLocal;
    }, [eventsLocal]);

    // -------------------
    // UPDATE RESOURCES
    // -------------------
    useEffect(() => {
      const cal = calendarApiRef.current;
      if (!cal || currentView === 'taskList') return;

      // Update resources using setOption with proper parent-child structure
      cal.setOption(
        'resources',
        myResources.map((resource) => ({
          ...resource,
          // Include parentId for hierarchical display
          ...((resource as any).parentId
            ? { parentId: (resource as any).parentId }
            : {}),
          extendedProps: {
            start: resource.start,
            end: resource.end,
            isEvent: resource.isEvent,
            // Check if this resource has children in the original data
            mainTask: myResources.some((r: any) => r.parentId === resource.id),
          },
        })),
      );
    }, [myResources, currentView]);

    // -------------------
    // UPDATE EVENTS
    // -------------------
    useEffect(() => {
      const cal = calendarApiRef.current;
      if (!cal || currentView === 'taskList') return;

      cal.batchRendering(() => {
        cal.removeAllEventSources();
        cal.addEventSource(
          myEvents.map((event) => ({
            ...event,
            resourceId: event.resourceId,
            className: 'custom-event',
          })),
        );
      });
    }, [myEvents, currentView]);

    // -------------------
    // CHANGE VIEW
    // -------------------
    useEffect(() => {
      const cal = calendarApiRef.current;
      if (cal && currentView !== 'taskList') cal.changeView(view);
    }, [view, currentView]);

    // Project view owns its range: no prev/next arrows and no custom-range pickers there.
    const isProjectView = view === PROJECT_VIEW;
    const rangePickerHandlers = isProjectView
      ? {}
      : {
          onStartDateChange: (date: string) => {
            userNavigatedRef.current = true;
            setTimelineStartDate(date);
          },
          onEndDateChange: (date: string) => {
            userNavigatedRef.current = true;
            setTimelineEndDate(date);
          },
          onClearRange: () => {
            userNavigatedRef.current = true;
            setTimelineStartDate(null);
            setTimelineEndDate(null);
          },
        };

    // Only show task list if we're in that specific view
    if (currentView === 'taskList') {
      return (
        <div data-project-id={projectId}>
          <CustomizedTables
            tasks={combinedArray}
            handleTaskDetail={handleTaskDetail}
            handleTaskEdit={setTask}
            handleEventDetail={handleEventDetail}
            handleEventEdit={handleEventDetail}
            onDateClick={handleNavigateToDate}
            reloadTasks={reloadTasks}
            taskBudgetRequests={taskBudgetRequests}
          />
        </div>
      );
    }

    // Only show calendar if we're in timeline or calendar view
    if (currentView === 'timeline' || currentView === 'calendar') {
      return (
        <div data-project-id={projectId} className="">
          {/* Drag Indicator */}
          {isDragging && (
            <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-[99999] flex items-center gap-2 animate-pulse">
              <MoveVertical className="h-4 w-4" />
              <span>
                {tSchedule.updatingTask} <strong>{draggingTaskName}</strong>
              </span>
            </div>
          )}
          <CalendarHeader
            title={calendarTitle}
            onPrevClick={handlePrevClick}
            onNextClick={handleNextClick}
            onTodayClick={handleTitleClick}
            showNavigation={!isProjectView}
            startDate={timelineStartDate}
            endDate={timelineEndDate}
            {...rangePickerHandlers}
          />
          {isProjectView && !projectReady && (
            <div
              data-testid="schedule-skeleton"
              aria-busy="true"
              className="flex flex-col gap-2 p-4">
              <Skeleton height={32} />
              <Skeleton height={32} />
              <Skeleton height={32} />
            </div>
          )}
          <div ref={calendarRef} />

          {/* Precedent Conflict Modal */}
          <PrecedentConflictModal
            isOpen={showPrecedentConflictModal}
            taskName={
              tasksLocal.find((t) => t.taskId === pendingDropData?.taskId)
                ?.name || ''
            }
            proposedStartDate={
              pendingDropData?.newStartDate?.toISOString() || ''
            }
            proposedEndDate={
              pendingDropData?.newEndDate instanceof Date
                ? pendingDropData.newEndDate.toISOString()
                : pendingDropData?.newEndDate || ''
            }
            conflicts={precedentConflicts}
            onCancel={handleConflictCancel}
            onConfirm={handleConflictConfirm}
            isLoading={isUpdatingTask}
            confirmLabel="MOVE ANYWAY"
          />

          {/* Subtask Conflict Modal (parent moved/resized) */}
          <SubtaskConflictModal
            isOpen={showSubtaskConflictModal}
            parentTaskName={
              tasksLocal.find((t) => t.taskId === pendingSubtaskDropData?.taskId)
                ?.name || ''
            }
            newParentStartDate={
              pendingSubtaskDropData?.newStartDate?.toISOString() || ''
            }
            newParentEndDate={
              pendingSubtaskDropData?.newEndDate instanceof Date
                ? pendingSubtaskDropData.newEndDate.toISOString()
                : ''
            }
            conflicts={subtaskConflicts}
            onAdjustSubtasks={handleAdjustSubtasks}
            onLeaveSubtasks={handleLeaveSubtasks}
            onCancel={handleSubtaskConflictCancel}
            isLoading={isUpdatingTask}
            mode={subtaskConflictMode}
          />

          {/* Subtask Out of Range Modal (subtask moved/resized outside parent) */}
          <SubtaskOutOfRangeModal
            isOpen={showSubtaskOutOfRangeModal}
            data={subtaskOutOfRangeData}
            mode={subtaskOutOfRangeMode}
            onExtendParent={handleExtendParent}
            onLeaveOutside={handleLeaveSubtaskOutside}
            onCancel={handleSubtaskOutOfRangeCancel}
            isLoading={isUpdatingTask}
          />

          {/* Delegated Task Modal */}
          <DelegatedTaskModal
            isOpen={showDelegatedTaskModal}
            data={delegatedTaskData}
            onCancel={handleDelegatedTaskCancel}
            onConfirm={handleDelegatedTaskConfirm}
            isLoading={isUpdatingTask}
          />
        </div>
      );
    }

    // If we're not in any specific view, show timeline by default
    return (
      <>
        {/* Drag Indicator */}
        {isDragging && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-[99999] flex items-center gap-2 animate-pulse">
            <MoveVertical className="h-4 w-4" />
            <span>
              {tSchedule.updatingTask} <strong>{draggingTaskName}</strong>
            </span>
          </div>
        )}
        <div
          data-project-id={projectId}
          style={{ maxWidth: '100%', minHeight: '400px' }}>
          <CalendarHeader
            title={calendarTitle}
            onPrevClick={handlePrevClick}
            onNextClick={handleNextClick}
            onTodayClick={handleTitleClick}
            showNavigation={!isProjectView}
            startDate={timelineStartDate}
            endDate={timelineEndDate}
            {...rangePickerHandlers}
          />
          <div
            ref={calendarRef}
            style={{ height: 'auto', minHeight: '350px' }}
          />
        </div>

        {/* Precedent Conflict Modal */}
        <PrecedentConflictModal
          isOpen={showPrecedentConflictModal}
          taskName={
            tasksLocal.find((t) => t.taskId === pendingDropData?.taskId)
              ?.name || ''
          }
          proposedStartDate={
            pendingDropData?.newStartDate?.toISOString() || ''
          }
          proposedEndDate={
            pendingDropData?.newEndDate instanceof Date
              ? pendingDropData.newEndDate.toISOString()
              : pendingDropData?.newEndDate || ''
          }
          conflicts={precedentConflicts}
          onCancel={handleConflictCancel}
          onConfirm={handleConflictConfirm}
          isLoading={isUpdatingTask}
          confirmLabel="MOVE ANYWAY"
        />

        {/* Subtask Conflict Modal (parent moved/resized) */}
        <SubtaskConflictModal
          isOpen={showSubtaskConflictModal}
          parentTaskName={
            tasksLocal.find((t) => t.taskId === pendingSubtaskDropData?.taskId)
              ?.name || ''
          }
          newParentStartDate={
            pendingSubtaskDropData?.newStartDate?.toISOString() || ''
          }
          newParentEndDate={
            pendingSubtaskDropData?.newEndDate instanceof Date
              ? pendingSubtaskDropData.newEndDate.toISOString()
              : ''
          }
          conflicts={subtaskConflicts}
          onAdjustSubtasks={handleAdjustSubtasks}
          onLeaveSubtasks={handleLeaveSubtasks}
          onCancel={handleSubtaskConflictCancel}
          isLoading={isUpdatingTask}
          mode={subtaskConflictMode}
        />

        {/* Subtask Out of Range Modal (subtask moved/resized outside parent) */}
        <SubtaskOutOfRangeModal
          isOpen={showSubtaskOutOfRangeModal}
          data={subtaskOutOfRangeData}
          mode={subtaskOutOfRangeMode}
          onExtendParent={handleExtendParent}
          onLeaveOutside={handleLeaveSubtaskOutside}
          onCancel={handleSubtaskOutOfRangeCancel}
          isLoading={isUpdatingTask}
        />

        {/* Delegated Task Modal */}
        <DelegatedTaskModal
          isOpen={showDelegatedTaskModal}
          data={delegatedTaskData}
          onCancel={handleDelegatedTaskCancel}
          onConfirm={handleDelegatedTaskConfirm}
          isLoading={isUpdatingTask}
        />
      </>
    );
  },
);
